import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { ISelectValue } from 'constants/globalTypes'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllOperationPlanAuditLogsSingleResponse,
  IGetAllOperationPlansSingleResponse,
  IGetSingleOperationPlanDashboardResponse,
  IGetSingleOperationPlanResponse,
  IOperatinoPlanPostPatchRequest,
  IProcessBindingConfigRequest,
  IProcessBindingConfigResponse,
  IProcessResponseComponent,
  ITaskCycleBindingConfigRequest,
  ITaskCycleBindingConfigResponse,
  ITaskCycleResponseComponent,
  IWorkflowBindingConfigRequest,
  IWorkflowBindingConfigResponse,
  IWorkflowItemsResponseComponent,
} from 'types/api/operationsPlansApi.interface'
import { currencies } from 'pages/Companies/AddCompany'
import {
  advanceRuleList,
  executableTypeList,
  boardVisibilityOptions,
  horizonTypeOptions,
  priorityOptions,
  workflowCriteriaList,
  zoneLevels,
} from 'pages/Operations/Plans/plansData'
import {
  IOperationPlanState,
  IPlansSliceInitalState,
  ITaskCycleBindingConfigState,
  IProcessBindingConfigState,
  IWorkflowBindingConfigState,
  IPhaseState,
} from 'types/operationPlans.interface'
import { baseUrl } from 'utils/axios'
import { getParams, handleErrors } from 'utils/helpers'
import { components } from 'pages/TaskTemplates/taskData'

const findOption = (options: { label: string; value: number }[], value: number) =>
  options.find((o) => o.value === value)

const formatBindingConfigFromResponse = async (
  executableType: number,
  bindingConfig:
    | IWorkflowBindingConfigResponse
    | IProcessBindingConfigResponse
    | ITaskCycleBindingConfigResponse
    | null
    | undefined,
): Promise<
  IWorkflowBindingConfigState | IProcessBindingConfigState | ITaskCycleBindingConfigState | null
> => {
  if (!bindingConfig) return null

  if (executableType === 2) {
    const getComponentValue = async (
      component: IWorkflowItemsResponseComponent,
    ): Promise<string | number | Date | ISelectValue> => {
      let value: string | number | Date | ISelectValue
      const foundComponent = components.find(
        (c) => c.component === component.taskComponent.component,
      )

      switch (foundComponent?.inputType) {
        case 1:
          value = component.value
          break
        case 2:
          value = Number(component.value)
          break
        case 3:
          value = new Date(String(component.value))
          break
        case 4:
          value = foundComponent.options.find((o) => o.value === component.value)
          break
        case 5:
          const resp = await baseUrl.get(foundComponent.link + component.value.toString())
          value = foundComponent.parserFunction(resp.data)
          break
      }
      return value
    }

    const binding = bindingConfig as IWorkflowBindingConfigResponse
    return {
      startDate: binding.startDate ? new Date(binding.startDate) : null,
      criteria: binding.criteria ? findOption(workflowCriteriaList, binding.criteria) : null,
      items:
        (await Promise.all(
          (binding.items ?? []).map(async (item) => ({
            workflowTemplateItem: {
              label: item.workflowTemplateItem.taskTemplate.name,
              value: item.workflowTemplateItem.id,
            },
            location: item.location ? { label: item.location.name, value: item.location.id } : null,
            componentValues:
              (await Promise.all(
                (item.componentValues ?? []).map(async (component) => ({
                  taskComponent: {
                    label: component.taskComponent.name,
                    value: component.taskComponent.id,
                  },
                  componentType: component.taskComponent.component,
                  value: await getComponentValue(component),
                })),
              )) ?? [],
            toolComponentValues:
              item.toolComponentValues?.map((component) => ({
                taskTemplateToolTypeComponent: {
                  label: component.toolTypeComponent.name,
                  value: component.toolTypeComponent.id,
                },
                toolComponent: {
                  label: component.toolTypeComponent.toolTypeComponent.name,
                  value: component.toolComponentId,
                },
                amount: component.amount,
              })) ?? [],
          })),
        )) ?? [],
    }
  }

  if (executableType === 1) {
    const getComponentValue = async (
      component: ITaskCycleResponseComponent,
    ): Promise<string | number | Date | ISelectValue> => {
      let value: string | number | Date | ISelectValue
      const foundComponent = components.find(
        (c) => c.component === component.taskCycleTemplateComponent.component,
      )

      switch (foundComponent?.inputType) {
        case 1:
          value = component.value
          break
        case 2:
          value = Number(component.value)
          break
        case 3:
          value = new Date(String(component.value))
          break
        case 4:
          value = foundComponent.options.find((o) => o.value === component.value)
          break
        case 5:
          const resp = await baseUrl.get(foundComponent.link + component.value.toString())
          value = foundComponent.parserFunction(resp.data)
          break
      }
      return value
    }

    const binding = bindingConfig as ITaskCycleBindingConfigResponse
    return {
      startDate: binding.startDate ? new Date(binding.startDate) : null,
      parallelismTarget: binding.parallelismTarget,
      site: binding.site ? { label: binding.site.name, value: binding.site.id } : null,
      componentsData:
        (await Promise.all(
          (binding.componentsData ?? []).map(async (component) => ({
            taskCycleTemplateComponent: {
              label: component.taskCycleTemplateComponent.name,
              value: component.taskCycleTemplateComponent.id,
            },
            componentType: component.taskCycleTemplateComponent.component,
            value: await getComponentValue(component),
          })),
        )) ?? [],
    }
  }

  if (executableType === 3) {
    const getComponentValue = async (
      component: IProcessResponseComponent,
    ): Promise<string | number | Date | ISelectValue> => {
      let value: string | number | Date | ISelectValue
      const foundComponent = components.find(
        (c) => c.component === component.processTemplateComponent.component,
      )

      switch (foundComponent?.inputType) {
        case 1:
          value = component.value
          break
        case 2:
          value = Number(component.value)
          break
        case 3:
          value = new Date(String(component.value))
          break
        case 4:
          value = foundComponent.options.find((o) => o.value === component.value)
          break
        case 5:
          const resp = await baseUrl.get(foundComponent.link + component.value.toString())
          value = foundComponent.parserFunction(resp.data)
          break
      }
      return value
    }

    const binding = bindingConfig as IProcessBindingConfigResponse
    return {
      startDate: binding.startDate ? new Date(binding.startDate) : null,
      location: binding.location
        ? {
            label:
              (binding.location as { name?: string; label?: string }).name ??
              binding.location.label,
            value:
              (binding.location as { id?: number; value?: number }).id ?? binding.location.value,
          }
        : null,
      componentsData:
        (await Promise.all(
          (binding.componentsData ?? []).map(async (component) => ({
            processTemplateComponent: {
              label: component.processTemplateComponent.name,
              value: component.processTemplateComponent.id,
            },
            componentType: component.processTemplateComponent.component,
            value: await getComponentValue(component),
          })),
        )) ?? [],
    }
  }

  return null
}

const initialState: IPlansSliceInitalState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singlePlan: null,
  dashboard: null,
  auditLogs: [],
  auditLogsTotalPages: 0,
  auditLogsTotalCount: 0,
}

export const activateOperationPlan = createAsyncThunk(
  'operationPlans/activateOperationPlan',
  async ({ id, single }: { id: number; single: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/activate`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const cancelOperationPlan = createAsyncThunk(
  'operationPlans/cancelOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/cancel`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const pauseOperationPlan = createAsyncThunk(
  'operationPlans/pauseOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/pause`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const completeOperationPlan = createAsyncThunk(
  'operationPlans/completeOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/complete`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const resumeOperationPlan = createAsyncThunk(
  'operationPlans/resumeOperationPlan',
  async ({ id, single }: { id: number; single?: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`aim-operations/plan/${id}/resume`)
      if (single) {
        await thunkAPI.dispatch(getSingleOperationPlan(id.toString()))
      } else {
        await thunkAPI.dispatch(getOperationPlans(getParams({ location })))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activateOperationPlanPhase = createAsyncThunk(
  'operationPlans/cancelOperationPlanPhase',
  async ({ planId, phaseId }: { planId: number; phaseId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/plan/${planId}/phases/${phaseId}/activate`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const completeOperationPlanPhase = createAsyncThunk(
  'operationPlans/completeOperationPlanPhase',
  async ({ planId, phaseId }: { planId: number; phaseId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/plan/${planId}/phases/${phaseId}/complete`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const skipOperationPlanPhase = createAsyncThunk(
  'operationPlans/skipOperationPlanPhase',
  async ({ planId, phaseId }: { planId: number; phaseId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `aim-operations/plan/${planId}/phases/${phaseId}/skip`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationPlans = createAsyncThunk(
  'operationPlans/getOperationPlans',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllOperationPlansSingleResponse>> =
        await baseUrl.get('aim-operations/plan', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationPlanAuditLogs = createAsyncThunk(
  'operationPlans/getOperationPlanAuditLogs',
  async ({ id, params }: { id: string; params: any }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllOperationPlanAuditLogsSingleResponse>> =
        await baseUrl.get(`aim-operations/plan/${id}/audit-logs`, {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getOperationPlanDashboard = createAsyncThunk(
  'operationPlans/getOperationPlanDashboard',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetSingleOperationPlanDashboardResponse> = await baseUrl.get(
        `aim-operations/plan/${id}/dashboard`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleOperationPlan = createAsyncThunk(
  'operationPlans/getSingleOperationPlan',
  async (id: string, thunkAPI) => {
    try {
      const {
        data,
      }: AxiosResponse<
        IGetSingleOperationPlanResponse<
          | IWorkflowBindingConfigResponse
          | IProcessBindingConfigResponse
          | ITaskCycleBindingConfigResponse
        >
      > = await baseUrl.get('aim-operations/plan/' + id)
      const planState: IOperationPlanState = {
        name: data.name,
        code: data.code,
        status: data.status,
        site: data.site ? { label: data.site.name, value: data.site.id } : null,
        owner: data.owner
          ? {
              label: `${data.owner.firstName} ${data.owner.lastName}`,
              value: data.owner.id,
            }
          : null,
        zoneScopeLevel: findOption(zoneLevels, data.zoneScopeLevel),
        zoneScopeIds: data.zoneScopeIds.map((zoneId) => ({
          label: String(zoneId),
          value: zoneId,
        })),
        priority: findOption(priorityOptions, data.priority),
        objective: data.objective,
        targetUnit: data.targetUnit
          ? { label: data.targetUnit.name, value: data.targetUnit.id }
          : null,
        targetValue: data.targetValue,
        targetBudgetCost: data.targetBudgetCost,
        targetCurrencyCode: data.targetCurrencyCode
          ? ({
              label:
                currencies.find((c) => c.value === data.targetCurrencyCode)?.label ??
                data.targetCurrencyCode.toString(),
              value: data.targetCurrencyCode,
            } as unknown as ISelectValue)
          : null,
        dueAt: data.dueAt ? new Date(data.dueAt) : null,
        boardVisibility: findOption(boardVisibilityOptions, data.boardVisibility),
        autoPauseOnL3Plus: data.autoPauseOnL3Plus,
        phases: await Promise.all(
          data.phases.map(async (phase) => ({
            id: phase.id,
            name: phase.name,
            status: phase.status,
            sequence: phase.sequence,
            executableType: executableTypeList.find((o) => o.value === phase.executableType),
            advanceRule: advanceRuleList.find((o) => o.value === phase.advanceRule),
            workflowTemplate: phase.workflowTemplate
              ? { label: phase.workflowTemplate.name, value: phase.workflowTemplate.id }
              : null,
            processTemplate: phase.processTemplate
              ? { label: phase.processTemplate.name, value: phase.processTemplate.id }
              : null,
            taskCycleTemplate: phase.taskCycleTemplate
              ? { label: phase.taskCycleTemplate.name, value: phase.taskCycleTemplate.id }
              : null,
            bindingConfig: await formatBindingConfigFromResponse(
              phase.executableType,
              phase.bindingConfig,
            ),
          })),
        ),
        horizon: {
          horizonType: data.horizon
            ? findOption(horizonTypeOptions, data.horizon.horizonType)
            : null,
          schedulerRunFreqMin: data.horizon?.schedulerRunFreqMin ?? null,
          replenishThreshold: data.horizon?.replenishThreshold ?? null,
          maxQueueSize: data.horizon?.maxQueueSize ?? null,
        },
      }
      return planState
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const formatBindingConfigForRequest = (
  phase: IPhaseState<
    IWorkflowBindingConfigState | IProcessBindingConfigState | ITaskCycleBindingConfigState
  >,
):
  | IWorkflowBindingConfigRequest
  | IProcessBindingConfigRequest
  | ITaskCycleBindingConfigRequest => {
  try {
    console.log(phase)
    if (phase.executableType?.value == 2) {
      const binding = phase.bindingConfig as IWorkflowBindingConfigState
      return {
        startDate: binding.startDate?.toISOString(),
        criteria: binding.criteria?.value as 1 | 2,
        items: binding.items?.map((item) => ({
          workflowTemplateItemId: item.workflowTemplateItem?.value,
          locationNodeId: item.location?.value,
          componentValues: item.componentValues?.map((component) => ({
            taskComponentId: component.taskComponent?.value,
            value:
              component.value instanceof Date
                ? component.value.toISOString()
                : typeof component.value === 'object'
                ? (component.value as ISelectValue)?.value
                : component.value,
          })),
          toolComponentValues: item.toolComponentValues?.map((component) => ({
            taskTemplateToolTypeComponentId: component.taskTemplateToolTypeComponent?.value,
            toolComponentId: component.toolComponent?.value,
            amount: component.amount,
          })),
        })),
      }
    }

    if (phase.executableType?.value == 1) {
      const binding = phase.bindingConfig as ITaskCycleBindingConfigState
      return {
        startDate: binding.startDate?.toISOString(),
        parallelismTarget: binding.parallelismTarget,
        siteNodeId: binding.site?.value,
        componentsData: binding.componentsData?.map((component) => ({
          taskCycleTemplateComponentId: component.taskCycleTemplateComponent?.value,
          value:
            component.value instanceof Date
              ? component.value.toISOString()
              : typeof component.value === 'object'
              ? (component.value as ISelectValue)?.value
              : component.value,
        })),
      }
    }

    if (phase.executableType?.value == 3) {
      const binding = phase.bindingConfig as IProcessBindingConfigState
      return {
        startDate: binding.startDate?.toISOString(),
        locationNodeId: binding.location?.value,
        componentsData: binding.componentsData?.map((component) => ({
          processTemplateComponentId: component.processTemplateComponent?.value,
          value:
            component.value instanceof Date
              ? component.value.toISOString()
              : typeof component.value === 'object'
              ? (component.value as ISelectValue)?.value
              : component.value,
        })),
      }
    }
  } catch (error) {
    console.log(error)
  }
}

export const addOperationPlan = createAsyncThunk(
  'operationPlans/addOperationPlan',
  async (state: IOperationPlanState, thunkAPI) => {
    const req: IOperatinoPlanPostPatchRequest<
      | IWorkflowBindingConfigRequest
      | IProcessBindingConfigRequest
      | ITaskCycleBindingConfigRequest
      | null
    > = {
      name: state.name,
      code: state.code,
      siteNodeId: state.site?.value,
      ownerId: state.owner?.value,
      zoneScopeLevel: state.zoneScopeLevel?.value,
      zoneScopeIds: state.zoneScopeIds?.map((id) => id?.value),
      priority: state.priority?.value,
      objective: state.objective,
      targetUnitId: state.targetUnit?.value,
      targetValue: state.targetValue,
      targetBudgetCost: state.targetBudgetCost,
      targetCurrencyCode: state.targetCurrencyCode?.value as unknown as string,
      dueAt: state.dueAt?.toISOString() || null,
      boardVisibility: state.boardVisibility?.value,
      autoPauseOnL3Plus: state.autoPauseOnL3Plus,
      horizon: state.horizon?.horizonType
        ? {
            horizonType: state.horizon.horizonType?.value,
            schedulerRunFreqMin: state.horizon?.schedulerRunFreqMin,
            replenishThreshold: state.horizon?.replenishThreshold,
            maxQueueSize: state.horizon?.maxQueueSize,
          }
        : null,
      phases: state.phases?.map((phase) => ({
        name: phase.name,
        sequence: phase.sequence,
        executableType: phase.executableType?.value,
        advanceRule: phase.advanceRule?.value,
        workflowTemplateId: phase.workflowTemplate?.value,
        processTemplateId: phase.processTemplate?.value,
        taskCycleTemplateId: phase.taskCycleTemplate?.value,
        bindingConfig: formatBindingConfigForRequest(phase),
      })),
    }

    try {
      const resp: AxiosResponse = await baseUrl.post('aim-operations/plan', req)
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editOperationPlan = createAsyncThunk(
  'operationPlans/editOperationPlan',
  async ({ id, state }: { id: string; state: IOperationPlanState }, thunkAPI) => {
    const req: IOperatinoPlanPostPatchRequest<
      IWorkflowBindingConfigRequest | IProcessBindingConfigRequest | ITaskCycleBindingConfigRequest
    > = {
      name: state.name,
      code: state.code,
      siteNodeId: state.site?.value,
      ownerId: state.owner?.value,
      zoneScopeLevel: state.zoneScopeLevel?.value,
      zoneScopeIds: state.zoneScopeIds?.map((id) => id?.value),
      priority: state.priority?.value,
      objective: state.objective,
      targetUnitId: state.targetUnit?.value,
      targetValue: state.targetValue,
      targetBudgetCost: state.targetBudgetCost,
      targetCurrencyCode: state.targetCurrencyCode?.value as unknown as string,
      dueAt: state.dueAt?.toISOString() || null,
      boardVisibility: state.boardVisibility?.value,
      autoPauseOnL3Plus: state.autoPauseOnL3Plus,
      horizon: state.horizon?.horizonType
        ? {
            horizonType: state.horizon.horizonType?.value,
            schedulerRunFreqMin: state.horizon?.schedulerRunFreqMin,
            replenishThreshold: state.horizon?.replenishThreshold,
            maxQueueSize: state.horizon?.maxQueueSize,
          }
        : null,
      phases: state.phases?.map((phase) => ({
        name: phase.name,
        sequence: phase.sequence,
        executableType: phase.executableType?.value,
        advanceRule: phase.advanceRule?.value,
        workflowTemplateId: phase.workflowTemplate?.value,
        processTemplateId: phase.processTemplate?.value,
        taskCycleTemplateId: phase.taskCycleTemplate?.value,
        bindingConfig: formatBindingConfigForRequest(phase),
      })),
    }

    try {
      const resp: AxiosResponse = await baseUrl.patch(`aim-operations/plan/${id}`, req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const operationPlansSlice = createSlice({
  name: 'operationPlans',
  initialState,
  reducers: {
    clearOperationPlans: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearSingleOperationPlan: (state) => {
      state.singlePlan = null
    },
    clearOperationPlanAuditLogs: (state) => {
      state.auditLogs = []
      state.auditLogsTotalCount = 0
      state.auditLogsTotalPages = 0
    },
  },
  extraReducers: {
    [getOperationPlans.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationPlans.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllOperationPlansSingleResponse> },
    ) => {
      state.isLoading = false
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getOperationPlans.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getOperationPlanAuditLogs.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationPlanAuditLogs.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllOperationPlanAuditLogsSingleResponse> },
    ) => {
      state.isLoading = false
      state.auditLogs = payload.data
      state.auditLogsTotalCount = payload.totalCount
      state.auditLogsTotalPages = payload.totalPages
    },
    [getOperationPlanAuditLogs.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getOperationPlanDashboard.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationPlanDashboard.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSingleOperationPlanDashboardResponse },
    ) => {
      state.isLoading = false
      state.dashboard = payload
    },
    [getOperationPlanDashboard.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleOperationPlan.fulfilled.type]: (
      state,
      { payload }: { payload: IOperationPlanState },
    ) => {
      state.singlePlan = payload
      state.isLoading = false
    },
    [getSingleOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [addOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan added successfully')
    },
    [addOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      console.log(payload)
      handleErrors(payload)
    },
    [editOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [editOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan edited successfully')
    },
    [editOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [activateOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [activateOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan activated successfully')
    },
    [activateOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [cancelOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [cancelOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan cancelled successfully')
    },
    [cancelOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [pauseOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [pauseOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan paused successfully')
    },
    [pauseOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [completeOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [completeOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan completed successfully')
    },
    [completeOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [resumeOperationPlan.pending.type]: (state) => {
      state.isLoading = true
    },
    [resumeOperationPlan.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Operation plan resumed successfully')
    },
    [resumeOperationPlan.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [activateOperationPlanPhase.pending.type]: (state) => {
      state.isLoading = true
    },
    [activateOperationPlanPhase.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Phase activated successfully')
    },
    [activateOperationPlanPhase.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [completeOperationPlanPhase.pending.type]: (state) => {
      state.isLoading = true
    },
    [completeOperationPlanPhase.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Phase completed successfully')
    },
    [completeOperationPlanPhase.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [skipOperationPlanPhase.pending.type]: (state) => {
      state.isLoading = true
    },
    [skipOperationPlanPhase.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Phase skipped successfully')
    },
    [skipOperationPlanPhase.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearOperationPlans, clearSingleOperationPlan, clearOperationPlanAuditLogs } =
  operationPlansSlice.actions

export default operationPlansSlice.reducer
